import { useRef } from "react";
import { Box } from "@mui/material";
import { AccountStub, Attachment } from "protogen/common_pb";
import { DirectMessage } from "protogen/messaging_service_pb";
import { isToday } from "../../common/utils";
import DateDisplay from "../common/DateDisplay";
import ActivityFeedEntry from "components/activity/ActivityFeedEntry";
import ActivityDivider from "components/activity/ActivityDivider";
import { CurrentUser } from "protogen/auth_pb";
import useIsMobile from "../hooks/useIsMobile";
import ClientOpportunityCard from "./AssignmentOpportunityCard";
import { useRespondToAssignment } from "services/signup";
import { useNavigate } from "react-router-dom";
type Props = {
  entry: DirectMessage;
  // Used to format things in a sequence.
  nextEntry?: DirectMessage;
  prevEntry?: DirectMessage;
  sender: AccountStub;
  openAttachment: (a: Attachment) => void;
  currentUser: CurrentUser;
  refresh?: () => void;
};

export default ({ entry, sender, prevEntry, currentUser, refresh }: Props) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const entryDt = new Date(Number(entry.timestampSec) * 1000);
  const time = DateDisplay({ date: entryDt });
  const boxRef = useRef(null);
  const { request: respondToAssignment, loading: respondingToAssignment } =
    useRespondToAssignment();
  // Helpful if you get confused.
  // console.log(entry.textMessage?.content, ' between ', prevEntry?.textMessage?.content, ' and ', nextEntry?.textMessage?.content);
  const SAME_TS_THRESHOLD_SEC = 10 * 60;
  const includeSender = !prevEntry || prevEntry.senderRef !== entry.senderRef;
  const includeTime =
    !prevEntry ||
    Number(entry.timestampSec) - Number(prevEntry.timestampSec) >
      SAME_TS_THRESHOLD_SEC;
  const includeHeader = includeSender || includeTime;
  // const includeAvatar = includeSender && sender && !sender.isAdvisor;
  const includeTodayDivider =
    isToday(entryDt) &&
    (!prevEntry || !isToday(new Date(Number(prevEntry.timestampSec) * 1000)));
  const buildHeader = (): string => {
    const senderName =
      currentUser.ref === entry.senderRef ? "You" : `${sender.displayName}`;
    return senderName;
  };
  const isSenderCurrentUser = sender.ref === currentUser.ref;
  const alignLeft = !isSenderCurrentUser;

  const handleAccept = async () => {
    const response = await respondToAssignment({
      assignmentRef: entry.assignmentOpportunity!.ref,
      responseStatus: "accepted",
      declinedReason: "",
    });
    if (response?.assignment?.familyRef) {
      navigate(
        `/families/${encodeURIComponent(
          response.assignment.familyRef,
        )}?newFamily=true`,
      );
    }
    refresh?.();
  };

  const handleDecline = async (reason: string | null) => {
    await respondToAssignment({
      assignmentRef: entry.assignmentOpportunity!.ref,
      responseStatus: "declined",
      declinedReason: reason || "",
    });
    refresh?.();
  };

  return (
    <>
      <ActivityFeedEntry
        alignLeft={alignLeft}
        avatarGutter={false}
        headerLeftText={includeHeader ? buildHeader() : null}
        headerRightText={includeHeader ? time : null}
      >
        <Box
          ref={boxRef}
          display={"flex"}
          flexDirection={"row"}
          width={"100%"}
          justifyContent={alignLeft ? "flex-start" : "flex-end"}
          alignItems={"flex-end"}
          gap={"8px"}
          sx={{
            position: "relative",
            ...(isMobile && { UserSelect: "none" }),
          }}
        >
          <ClientOpportunityCard
            assignmentOpportunity={entry.assignmentOpportunity!}
            senderView={isSenderCurrentUser}
            onAccept={handleAccept}
            onDecline={handleDecline}
            disabled={respondingToAssignment}
          />
        </Box>
      </ActivityFeedEntry>
      {includeTodayDivider && <ActivityDivider text={"Today"} />}
    </>
  );
};
